import { useState } from "react";
import { useLocation } from "react-router-dom";

import { NetworkStatus, useLazyQuery } from "@apollo/client";

import { useDateRange } from "../../../../common/components/datePicker/modelsDatePickers";
import { useCustomerIdGuard } from "../../../../common/hooks/useCustomerIdGuard";
import { RouteEnum } from "../../../../common/models/enums";
import { GetRecentEventsParams } from "../../../../common/types/events/params";
import { RecordingsApiResponse } from "../../../../common/types/recordings/api";
import { buildEventQueryVariables } from "../../../../common/utils/buildEventQueryVariables";
import { handleApolloError } from "../../../../common/utils/handleApolloError";
import { errorNotification } from "../../../../common/variables/notification";
import {
  useSelectedLocation,
  useSelectedTagLocationIds,
} from "../../../../common/variables/selectedLocation";
import { useSelectedModel } from "../../../../common/variables/selectedModel";
import { GET_RECORDINGS_BY_MODEL_AND_FACILITY } from "../../../../rest/queries";
import { useSelectedDataHubCamera } from "../../../data-hub/components/dataHub.variable";
import { mapRecordingToEventWithRecording } from "../../adapters/recordingToEventWithRecordingAdapter";
import { EventWithRecordingViewModel } from "../../models/eventWithRecordingViewModel";
import {
  DEFAULT_EXPORT_PAGE_SIZE,
  getDefaultFromDate,
  normalizeDeviceId,
  normalizeLocationId,
} from "./exportFilters";
import { HARDHAT_SELECT_OPTION } from "./useGetEventsToExport";

export const useGetRecordingsToExport = () => {
  const { pathname } = useLocation();

  const customerId = useCustomerIdGuard();
  const facilityId = useSelectedLocation();
  const tagLocationIds = useSelectedTagLocationIds();
  const dateRange = useDateRange();
  let modelId = useSelectedModel();
  const camera = useSelectedDataHubCamera();

  if (
    pathname === RouteEnum.TelemetryHardHat ||
    pathname === RouteEnum.SensoryTelemetryHardHat ||
    pathname === RouteEnum.HardHat ||
    pathname === RouteEnum.SensoryHardHat
  ) {
    modelId = HARDHAT_SELECT_OPTION;
  }

  // Match EventTableContainer / useGetEventsToExport: when a tag filter is
  // active, don't scope the API by facilityId — narrow client-side instead so
  // the export aligns with what's rendered in the table.
  const shouldUseTagFilter = !!(tagLocationIds && tagLocationIds.length > 0);

  const params: GetRecentEventsParams = {
    customerId,
    modelId: modelId?.value?.toLowerCase() ?? "",
    facilityId: shouldUseTagFilter ? "" : facilityId?.value ?? "",
    fromDate: dateRange?.from
      ? dateRange.from.toISOString()
      : getDefaultFromDate(),
    toDate: dateRange?.to
      ? dateRange.to.toISOString()
      : new Date().toISOString(),
    pageSize: DEFAULT_EXPORT_PAGE_SIZE,
  };

  const variables = buildEventQueryVariables(params);

  const [loading, setLoading] = useState(false);

  const [fetchRecordings, { error, networkStatus }] = useLazyQuery<{
    recordings: RecordingsApiResponse;
  }>(GET_RECORDINGS_BY_MODEL_AND_FACILITY, {
    variables,
    fetchPolicy: "no-cache",
    onError: handleApolloError,
  });

  const getRecordings = async (): Promise<EventWithRecordingViewModel[]> => {
    setLoading(true);

    try {
      const initialResponse = await fetchRecordings({
        variables: {
          ...variables,
          lastEvaluatedKey: "",
        },
      });

      let allRecordings = initialResponse.data?.recordings?.data ?? [];

      let currentLastEvaluatedKey =
        initialResponse.data?.recordings?.lastEvaluatedKey;

      while (currentLastEvaluatedKey) {
        const moreResponse = await fetchRecordings({
          variables: {
            ...variables,
            pageSize: DEFAULT_EXPORT_PAGE_SIZE,
            lastEvaluatedKey: encodeURIComponent(
              JSON.stringify(currentLastEvaluatedKey)
            ),
          },
        });

        const newRecordings = moreResponse.data?.recordings?.data ?? [];

        allRecordings = [...allRecordings, ...newRecordings];

        currentLastEvaluatedKey =
          moreResponse.data?.recordings?.lastEvaluatedKey;
      }

      let mapped = allRecordings.map(mapRecordingToEventWithRecording);

      const selectedCamera = normalizeDeviceId(camera?.value ?? "");

      if (selectedCamera) {
        mapped = mapped.filter(({ event, newestRecording }) => {
          const fromEvent = normalizeDeviceId(event.camera_id ?? "");
          const fromRecording = normalizeDeviceId(
            newestRecording?.rawEventInfo?.data?.camera_id ??
              newestRecording?.rawEventInfo?.data?.instance_name ??
              ""
          );

          return (
            fromEvent === selectedCamera || fromRecording === selectedCamera
          );
        });
      }

      if (shouldUseTagFilter) {
        const normalizedTagIds = tagLocationIds!.map(normalizeLocationId);

        mapped = mapped.filter(({ event }) => {
          const eventLoc = normalizeLocationId(
            event.facilityId ?? event.location ?? ""
          );

          return normalizedTagIds.includes(eventLoc);
        });
      }

      setLoading(false);

      return mapped;
    } catch (err) {
      console.error("Failed to fetch all recordings for export:", err);

      errorNotification("Failed to fetch recordings for export");

      setLoading(false);

      return [];
    }
  };

  return {
    getRecordings,
    loading: loading || networkStatus === NetworkStatus.fetchMore,
    error,
  } as const;
};
